import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config/constants';

export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'BootScene' });
  }

  preload(): void {
    // Nothing to preload - we generate textures in create()
  }

  create(): void {
    // Show loading text
    const loadingText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'Loading...', {
      fontFamily: 'Arial',
      fontSize: '24px',
      color: '#ffff00',
    });
    loadingText.setOrigin(0.5, 0.5);

    // Generate placeholder graphics
    this.createPlaceholderAssets();

    // Small delay to ensure textures are ready, then transition to Home
    this.time.delayedCall(100, () => {
      loadingText.destroy();
      this.scene.start('SplashScene');
    });
  }

  private createPlaceholderAssets(): void {
    // Create detailed Toaster Truck sprite
    const truckCanvas = this.textures.createCanvas('truck', 80, 64);
    if (truckCanvas) {
      const ctx = truckCanvas.getContext();
      
      // === MAIN BODY ===
      // Body gradient (pale yellow)
      const bodyGradient = ctx.createLinearGradient(8, 16, 8, 52);
      bodyGradient.addColorStop(0, '#f0e068');
      bodyGradient.addColorStop(0.5, '#e8d84c');
      bodyGradient.addColorStop(1, '#d4c43c');
      
      ctx.fillStyle = bodyGradient;
      ctx.beginPath();
      ctx.moveTo(12, 52);
      ctx.lineTo(12, 28);
      ctx.quadraticCurveTo(12, 18, 22, 18);
      ctx.lineTo(28, 18);
      ctx.lineTo(28, 16);
      ctx.lineTo(68, 16);
      ctx.quadraticCurveTo(72, 16, 72, 20);
      ctx.lineTo(72, 48);
      ctx.quadraticCurveTo(72, 52, 68, 52);
      ctx.lineTo(12, 52);
      ctx.fill();
      
      ctx.strokeStyle = '#b8a830';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      
      // === TOASTER SLOTS ===
      ctx.fillStyle = '#5a4a3a';
      ctx.fillRect(32, 8, 14, 10);
      ctx.fillRect(52, 8, 14, 10);
      
      ctx.fillStyle = '#3a2a1a';
      ctx.fillRect(34, 10, 10, 6);
      ctx.fillRect(54, 10, 10, 6);
      
      // === TOAST SLICES ===
      ctx.fillStyle = '#d4a574';
      ctx.fillRect(35, 0, 8, 12);
      ctx.fillRect(55, 3, 8, 10);
      
      ctx.strokeStyle = '#a07850';
      ctx.lineWidth = 1;
      ctx.strokeRect(35, 0, 8, 12);
      ctx.strokeRect(55, 3, 8, 10);
      
      // === WINDSHIELD ===
      ctx.fillStyle = '#4a6a7a';
      ctx.fillRect(14, 20, 12, 14);
      ctx.strokeStyle = '#c0c0c0';
      ctx.strokeRect(14, 20, 12, 14);
      
      // === SIDE PANEL (decal area) ===
      ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
      ctx.fillRect(30, 22, 36, 20);
      
      // === STARBURST DECORATIONS ===
      this.drawStarburst(ctx, 36, 28, 4, '#00d4d4', 6);
      this.drawStarburst(ctx, 58, 26, 3, '#ff8040', 6);
      this.drawStarburst(ctx, 48, 36, 3, '#00d4d4', 4);
      this.drawStarburst(ctx, 40, 38, 2, '#ffaa40', 4);
      
      // === FENDERS ===
      ctx.fillStyle = bodyGradient;
      ctx.beginPath();
      ctx.arc(20, 52, 10, Math.PI, 0, false);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(60, 52, 10, Math.PI, 0, false);
      ctx.fill();
      
      // === WHEELS ===
      this.drawWheel(ctx, 20, 54);
      this.drawWheel(ctx, 60, 54);
      
      // === HEADLIGHT ===
      ctx.fillStyle = '#ffff99';
      ctx.beginPath();
      ctx.arc(14, 42, 3, 0, Math.PI * 2);
      ctx.fill();
      
      // === REAR BUMPER ===
      ctx.fillStyle = '#4a7a8a';
      ctx.fillRect(68, 40, 8, 14);
      
      // === CHROME TRIM ===
      ctx.strokeStyle = '#e0e0e0';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(12, 44);
      ctx.lineTo(68, 44);
      ctx.stroke();
      
      truckCanvas.refresh();
    }

    // Create Toast projectile
    const toastCanvas = this.textures.createCanvas('toast', 16, 20);
    if (toastCanvas) {
      const ctx = toastCanvas.getContext();
      
      ctx.fillStyle = '#d4a574';
      ctx.fillRect(1, 1, 14, 18);
      
      ctx.strokeStyle = '#b8956e';
      ctx.lineWidth = 2;
      ctx.strokeRect(1, 1, 14, 18);
      
      ctx.strokeStyle = '#c9a87c';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(4, 5);
      ctx.lineTo(12, 5);
      ctx.moveTo(4, 10);
      ctx.lineTo(12, 10);
      ctx.moveTo(4, 15);
      ctx.lineTo(12, 15);
      ctx.stroke();
      
      toastCanvas.refresh();
    }

    // Create wheel glow texture
    const glowCanvas = this.textures.createCanvas('wheelGlow', 32, 32);
    if (glowCanvas) {
      const ctx = glowCanvas.getContext();
      
      const gradient = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
      gradient.addColorStop(0, 'rgba(255, 255, 0, 0.9)');
      gradient.addColorStop(0.3, 'rgba(255, 255, 0, 0.6)');
      gradient.addColorStop(0.6, 'rgba(255, 255, 0, 0.3)');
      gradient.addColorStop(1, 'rgba(255, 255, 0, 0)');
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(16, 16, 16, 0, Math.PI * 2);
      ctx.fill();
      
      glowCanvas.refresh();
    }

    // Create Evil Egg enemy sprite
    const eggCanvas = this.textures.createCanvas('evilEgg', 32, 32);
    if (eggCanvas) {
      const ctx = eggCanvas.getContext();
      
      // Egg white
      ctx.fillStyle = '#f8f8f0';
      ctx.beginPath();
      ctx.ellipse(16, 18, 14, 12, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#e8d8c0';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Yolk
      const yolkGradient = ctx.createRadialGradient(16, 16, 0, 16, 16, 8);
      yolkGradient.addColorStop(0, '#ffdd44');
      yolkGradient.addColorStop(0.7, '#ffaa00');
      yolkGradient.addColorStop(1, '#ee8800');
      ctx.fillStyle = yolkGradient;
      ctx.beginPath();
      ctx.arc(16, 16, 8, 0, Math.PI * 2);
      ctx.fill();
      
      // Angry eyebrows
      ctx.strokeStyle = '#442200';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(10, 12);
      ctx.lineTo(14, 14);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(22, 12);
      ctx.lineTo(18, 14);
      ctx.stroke();
      
      // Eyes
      ctx.fillStyle = '#000000';
      ctx.beginPath();
      ctx.arc(12, 16, 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(20, 16, 2, 0, Math.PI * 2);
      ctx.fill();
      
      // Mouth
      ctx.strokeStyle = '#442200';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(16, 22, 4, 0.2, Math.PI - 0.2);
      ctx.stroke();
      
      eggCanvas.refresh();
    }

    // Create Yolk projectile
    const yolkCanvas = this.textures.createCanvas('yolkProjectile', 12, 16);
    if (yolkCanvas) {
      const ctx = yolkCanvas.getContext();
      
      const yolkGradient = ctx.createRadialGradient(6, 6, 0, 6, 8, 8);
      yolkGradient.addColorStop(0, '#ffee44');
      yolkGradient.addColorStop(0.6, '#ffaa00');
      yolkGradient.addColorStop(1, '#dd8800');
      ctx.fillStyle = yolkGradient;
      
      // Drip shape
      ctx.beginPath();
      ctx.moveTo(6, 0);
      ctx.quadraticCurveTo(12, 4, 10, 10);
      ctx.quadraticCurveTo(8, 16, 6, 16);
      ctx.quadraticCurveTo(4, 16, 2, 10);
      ctx.quadraticCurveTo(0, 4, 6, 0);
      ctx.fill();
      
      // Highlight
      ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.beginPath();
      ctx.arc(4, 4, 2, 0, Math.PI * 2);
      ctx.fill();
      
      yolkCanvas.refresh();
    }

    // Create particle texture
    const particleCanvas = this.textures.createCanvas('particle', 8, 8);
    if (particleCanvas) {
      const ctx = particleCanvas.getContext();
      
      const gradient = ctx.createRadialGradient(4, 4, 0, 4, 4, 4);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
      gradient.addColorStop(0.5, 'rgba(255, 200, 100, 0.8)');
      gradient.addColorStop(1, 'rgba(255, 100, 0, 0)');
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(4, 4, 4, 0, Math.PI * 2);
      ctx.fill();
      
      particleCanvas.refresh();
    }

    // === PHASE 3 ENEMIES ===

    // Create Angry Bacon enemy sprite
    const baconCanvas = this.textures.createCanvas('angryBacon', 40, 24);
    if (baconCanvas) {
      const ctx = baconCanvas.getContext();
      
      // Bacon strip (wavy shape)
      const baconGradient = ctx.createLinearGradient(0, 0, 0, 24);
      baconGradient.addColorStop(0, '#c44');
      baconGradient.addColorStop(0.3, '#a33');
      baconGradient.addColorStop(0.5, '#faa');
      baconGradient.addColorStop(0.7, '#a33');
      baconGradient.addColorStop(1, '#822');
      
      ctx.fillStyle = baconGradient;
      ctx.beginPath();
      ctx.moveTo(2, 8);
      ctx.quadraticCurveTo(10, 4, 20, 8);
      ctx.quadraticCurveTo(30, 4, 38, 8);
      ctx.lineTo(38, 16);
      ctx.quadraticCurveTo(30, 20, 20, 16);
      ctx.quadraticCurveTo(10, 20, 2, 16);
      ctx.closePath();
      ctx.fill();
      
      // Angry eyes
      ctx.fillStyle = '#000';
      ctx.beginPath();
      ctx.arc(14, 11, 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(26, 11, 2, 0, Math.PI * 2);
      ctx.fill();
      
      // Angry eyebrows
      ctx.strokeStyle = '#400';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(10, 7);
      ctx.lineTo(16, 9);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(30, 7);
      ctx.lineTo(24, 9);
      ctx.stroke();
      
      // Grimace mouth
      ctx.strokeStyle = '#400';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(16, 16);
      ctx.lineTo(20, 14);
      ctx.lineTo(24, 16);
      ctx.stroke();
      
      baconCanvas.refresh();
    }

    // Create Grease projectile
    const greaseCanvas = this.textures.createCanvas('greaseProjectile', 10, 12);
    if (greaseCanvas) {
      const ctx = greaseCanvas.getContext();
      
      ctx.fillStyle = '#a64';
      ctx.beginPath();
      ctx.ellipse(5, 6, 4, 6, 0, 0, Math.PI * 2);
      ctx.fill();
      
      // Highlight
      ctx.fillStyle = 'rgba(255, 200, 150, 0.5)';
      ctx.beginPath();
      ctx.arc(3, 4, 2, 0, Math.PI * 2);
      ctx.fill();
      
      greaseCanvas.refresh();
    }

    // Create Menacing Coffee enemy sprite
    const coffeeCanvas = this.textures.createCanvas('menacingCoffee', 28, 36);
    if (coffeeCanvas) {
      const ctx = coffeeCanvas.getContext();
      
      // Cup body
      ctx.fillStyle = '#f8f8f8';
      ctx.beginPath();
      ctx.moveTo(4, 10);
      ctx.lineTo(2, 32);
      ctx.lineTo(26, 32);
      ctx.lineTo(24, 10);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#ccc';
      ctx.lineWidth = 1;
      ctx.stroke();
      
      // Coffee inside
      ctx.fillStyle = '#3a2010';
      ctx.fillRect(5, 12, 18, 18);
      
      // Steam wisps
      ctx.strokeStyle = 'rgba(200, 200, 200, 0.6)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(10, 8);
      ctx.quadraticCurveTo(8, 4, 10, 0);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(18, 8);
      ctx.quadraticCurveTo(20, 4, 18, 0);
      ctx.stroke();
      
      // Handle
      ctx.strokeStyle = '#ddd';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(26, 20, 5, -Math.PI/2, Math.PI/2);
      ctx.stroke();
      
      // Evil red eyes
      ctx.fillStyle = '#f00';
      ctx.beginPath();
      ctx.arc(10, 18, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(18, 18, 3, 0, Math.PI * 2);
      ctx.fill();
      
      // Evil pupils
      ctx.fillStyle = '#000';
      ctx.beginPath();
      ctx.arc(10, 18, 1.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(18, 18, 1.5, 0, Math.PI * 2);
      ctx.fill();
      
      // Sinister smile
      ctx.strokeStyle = '#200';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(14, 24, 5, 0.2, Math.PI - 0.2);
      ctx.stroke();
      
      coffeeCanvas.refresh();
    }

    // Create Coffee projectile
    const coffeeDropCanvas = this.textures.createCanvas('coffeeProjectile', 10, 14);
    if (coffeeDropCanvas) {
      const ctx = coffeeDropCanvas.getContext();
      
      ctx.fillStyle = '#3a2010';
      ctx.beginPath();
      ctx.moveTo(5, 0);
      ctx.quadraticCurveTo(10, 5, 8, 10);
      ctx.quadraticCurveTo(5, 14, 2, 10);
      ctx.quadraticCurveTo(0, 5, 5, 0);
      ctx.fill();
      
      // Highlight
      ctx.fillStyle = 'rgba(100, 80, 60, 0.5)';
      ctx.beginPath();
      ctx.arc(3, 4, 2, 0, Math.PI * 2);
      ctx.fill();
      
      coffeeDropCanvas.refresh();
    }

    // Create Classic Alien sprites (pixelated style)
    this.createAlienSprite('alien_squid', '#0f0', 'squid');
    this.createAlienSprite('alien_crab', '#0ff', 'crab');
    this.createAlienSprite('alien_octopus', '#f0f', 'octopus');

    // Create Alien laser projectile
    const laserCanvas = this.textures.createCanvas('alienLaser', 6, 14);
    if (laserCanvas) {
      const ctx = laserCanvas.getContext();
      
      // Pixelated zigzag laser
      ctx.fillStyle = '#0f0';
      ctx.fillRect(2, 0, 2, 3);
      ctx.fillRect(0, 3, 2, 3);
      ctx.fillRect(2, 6, 2, 3);
      ctx.fillRect(4, 9, 2, 3);
      ctx.fillRect(2, 12, 2, 2);
      
      laserCanvas.refresh();
    }

    // === PHASE 4 POWER-UPS ===
    
    // Rapid Fire power-up (orange bolt)
    const rapidFireCanvas = this.textures.createCanvas('powerup_rapidfire', 28, 28);
    if (rapidFireCanvas) {
      const ctx = rapidFireCanvas.getContext();
      
      // Background circle
      ctx.fillStyle = '#ff6600';
      ctx.beginPath();
      ctx.arc(14, 14, 13, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.strokeStyle = '#ffaa00';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Lightning bolt
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(16, 4);
      ctx.lineTo(10, 14);
      ctx.lineTo(14, 14);
      ctx.lineTo(12, 24);
      ctx.lineTo(20, 12);
      ctx.lineTo(16, 12);
      ctx.closePath();
      ctx.fill();
      
      rapidFireCanvas.refresh();
    }

    // Butter Shield power-up (yellow shield)
    const shieldCanvas = this.textures.createCanvas('powerup_shield', 28, 28);
    if (shieldCanvas) {
      const ctx = shieldCanvas.getContext();
      
      // Background circle
      ctx.fillStyle = '#ffdd00';
      ctx.beginPath();
      ctx.arc(14, 14, 13, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.strokeStyle = '#ffff44';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Shield shape (butter pat)
      ctx.fillStyle = '#fff8cc';
      ctx.fillRect(7, 8, 14, 12);
      
      ctx.strokeStyle = '#ddcc00';
      ctx.lineWidth = 1;
      ctx.strokeRect(7, 8, 14, 12);
      
      // Lines on butter
      ctx.strokeStyle = '#eedd66';
      ctx.beginPath();
      ctx.moveTo(9, 11);
      ctx.lineTo(19, 11);
      ctx.moveTo(9, 14);
      ctx.lineTo(19, 14);
      ctx.moveTo(9, 17);
      ctx.lineTo(19, 17);
      ctx.stroke();
      
      shieldCanvas.refresh();
    }

    // Jam Spread power-up (pink spread)
    const spreadCanvas = this.textures.createCanvas('powerup_spread', 28, 28);
    if (spreadCanvas) {
      const ctx = spreadCanvas.getContext();
      
      // Background circle
      ctx.fillStyle = '#ff0066';
      ctx.beginPath();
      ctx.arc(14, 14, 13, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.strokeStyle = '#ff4488';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Three arrows spreading
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      
      // Center arrow
      ctx.beginPath();
      ctx.moveTo(14, 6);
      ctx.lineTo(14, 20);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(10, 10);
      ctx.lineTo(14, 6);
      ctx.lineTo(18, 10);
      ctx.fill();
      
      // Left arrow
      ctx.beginPath();
      ctx.moveTo(6, 18);
      ctx.lineTo(10, 10);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(4, 12);
      ctx.lineTo(6, 8);
      ctx.lineTo(10, 10);
      ctx.fill();
      
      // Right arrow
      ctx.beginPath();
      ctx.moveTo(22, 18);
      ctx.lineTo(18, 10);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(24, 12);
      ctx.lineTo(22, 8);
      ctx.lineTo(18, 10);
      ctx.fill();
      
      spreadCanvas.refresh();
    }

    // Shield visual effect (bubble around truck)
    const shieldEffectCanvas = this.textures.createCanvas('shieldEffect', 100, 80);
    if (shieldEffectCanvas) {
      const ctx = shieldEffectCanvas.getContext();
      
      const gradient = ctx.createRadialGradient(50, 40, 20, 50, 40, 48);
      gradient.addColorStop(0, 'rgba(255, 221, 0, 0)');
      gradient.addColorStop(0.7, 'rgba(255, 221, 0, 0.1)');
      gradient.addColorStop(0.9, 'rgba(255, 255, 68, 0.4)');
      gradient.addColorStop(1, 'rgba(255, 255, 100, 0.6)');
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.ellipse(50, 40, 48, 38, 0, 0, Math.PI * 2);
      ctx.fill();
      
      shieldEffectCanvas.refresh();
    }

    // Create Barrier sprite
    const barrierCanvas = this.textures.createCanvas('barrier', 60, 40);
    if (barrierCanvas) {
      const ctx = barrierCanvas.getContext();
      
      // Diner counter style barrier
      const gradient = ctx.createLinearGradient(0, 0, 0, 40);
      gradient.addColorStop(0, '#c0c0c0');
      gradient.addColorStop(0.3, '#e0e0e0');
      gradient.addColorStop(0.5, '#f0f0f0');
      gradient.addColorStop(0.7, '#e0e0e0');
      gradient.addColorStop(1, '#a0a0a0');
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 60, 40);
      
      // Red checkerboard pattern
      ctx.fillStyle = '#c22';
      for (let row = 0; row < 4; row++) {
        for (let col = 0; col < 6; col++) {
          if ((row + col) % 2 === 0) {
            ctx.fillRect(col * 10, row * 10, 10, 10);
          }
        }
      }
      
      // Chrome trim top
      ctx.fillStyle = '#e8e8e8';
      ctx.fillRect(0, 0, 60, 4);
      
      // Chrome trim bottom
      ctx.fillStyle = '#888';
      ctx.fillRect(0, 36, 60, 4);
      
      barrierCanvas.refresh();
    }
  }

  private createAlienSprite(key: string, color: string, type: string): void {
    const canvas = this.textures.createCanvas(key, 32, 32);
    if (canvas) {
      const ctx = canvas.getContext();
      ctx.fillStyle = color;
      
      // Pixelated alien shapes (8x8 scaled up)
      const patterns: Record<string, number[][]> = {
        squid: [
          [0,0,0,1,1,0,0,0],
          [0,0,1,1,1,1,0,0],
          [0,1,1,1,1,1,1,0],
          [1,1,0,1,1,0,1,1],
          [1,1,1,1,1,1,1,1],
          [0,0,1,0,0,1,0,0],
          [0,1,0,1,1,0,1,0],
          [1,0,1,0,0,1,0,1],
        ],
        crab: [
          [0,0,1,0,0,1,0,0],
          [0,0,0,1,1,0,0,0],
          [0,0,1,1,1,1,0,0],
          [0,1,0,1,1,0,1,0],
          [1,1,1,1,1,1,1,1],
          [1,0,1,1,1,1,0,1],
          [1,0,1,0,0,1,0,1],
          [0,0,0,1,1,0,0,0],
        ],
        octopus: [
          [0,0,0,1,1,0,0,0],
          [0,0,1,1,1,1,0,0],
          [0,1,1,1,1,1,1,0],
          [1,1,0,1,1,0,1,1],
          [1,1,1,1,1,1,1,1],
          [0,1,0,1,1,0,1,0],
          [1,0,0,0,0,0,0,1],
          [0,1,0,0,0,0,1,0],
        ],
      };
      
      const pattern = patterns[type] || patterns.crab;
      const pixelSize = 4;
      
      for (let row = 0; row < 8; row++) {
        for (let col = 0; col < 8; col++) {
          if (pattern[row][col]) {
            ctx.fillRect(col * pixelSize, row * pixelSize, pixelSize, pixelSize);
          }
        }
      }
      
      canvas.refresh();
    }
  }

  private drawStarburst(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    color: string,
    points: number
  ): void {
    ctx.fillStyle = color;
    ctx.beginPath();
    for (let i = 0; i < points * 2; i++) {
      const radius = i % 2 === 0 ? size : size * 0.4;
      const angle = (i * Math.PI) / points - Math.PI / 2;
      const px = x + Math.cos(angle) * radius;
      const py = y + Math.sin(angle) * radius;
      if (i === 0) {
        ctx.moveTo(px, py);
      } else {
        ctx.lineTo(px, py);
      }
    }
    ctx.closePath();
    ctx.fill();
  }

  private drawWheel(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // Tire
    ctx.fillStyle = '#2a2a2a';
    ctx.beginPath();
    ctx.arc(x, y, 9, 0, Math.PI * 2);
    ctx.fill();
    
    // Whitewall
    ctx.fillStyle = '#f8f8f8';
    ctx.beginPath();
    ctx.arc(x, y, 7, 0, Math.PI * 2);
    ctx.fill();
    
    // Hub
    ctx.fillStyle = '#a88044';
    ctx.beginPath();
    ctx.arc(x, y, 5, 0, Math.PI * 2);
    ctx.fill();
    
    // Center cap
    ctx.fillStyle = '#c0c0c0';
    ctx.beginPath();
    ctx.arc(x, y, 1.5, 0, Math.PI * 2);
    ctx.fill();
  }
}
